import { useEffect, useState } from 'react';
import {
    Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip, Skeleton, Stack, Box, Button, Divider,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../utils/axios';
import { formatPrice, formatDate, orderStatusLabel } from '../utils/format';
import { b2bColors } from '../styles/b2bTheme';
import { getErrorMessage, buildErrorSnackbarOptions } from '../utils/errorUtils';

export default function OrderDetail() {
    const { id } = useParams();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [order, setOrder] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/b2b/orders/${id}`);
                if (cancelled) return;
                setOrder(data.order);
            } catch (err) {
                if (cancelled) return;
                if (err?.response?.status === 404) {
                    setNotFound(true);
                } else {
                    enqueueSnackbar(getErrorMessage(err, 'Chargement impossible'), buildErrorSnackbarOptions(err, closeSnackbar));
                }
            }
        })();
        return () => { cancelled = true; };
    }, [id, enqueueSnackbar, closeSnackbar]);

    if (notFound) {
        return (
            <Stack spacing={2}>
                <Typography variant="h1">Commande introuvable</Typography>
                <Typography variant="body1">Cette commande n'existe pas ou ne vous est pas accessible.</Typography>
                <Box>
                    <Button component={RouterLink} to="/orders" variant="contained">← Retour à mes commandes</Button>
                </Box>
            </Stack>
        );
    }

    if (!order) {
        return (
            <Stack spacing={2}>
                <Skeleton width={240} height={40} />
                <Paper sx={{ p: 2 }}>
                    <Stack spacing={1}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={36} variant="rectangular" />)}</Stack>
                </Paper>
            </Stack>
        );
    }

    return (
        <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2}>
                <Box>
                    <Typography variant="body2" sx={{ color: b2bColors.textMuted, fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        Commande
                    </Typography>
                    <Typography variant="h1" className="b2b-num">{order.order_number}</Typography>
                    <Typography variant="body2">Passée le {formatDate(order.order_date)}</Typography>
                </Box>
                <Chip label={orderStatusLabel(order.status)} color={order.status === 'DRAFT' ? 'default' : 'primary'} />
            </Stack>

            <Paper>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Produit</TableCell>
                            <TableCell align="right" className="b2b-num">Qté</TableCell>
                            <TableCell align="right" className="b2b-num">Prix unit.</TableCell>
                            <TableCell align="right" className="b2b-num">Taxe</TableCell>
                            <TableCell align="right" className="b2b-num">Ligne</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(order.lines || []).map((l, idx) => (
                            <TableRow key={idx}>
                                <TableCell>
                                    {l.sku && <Typography variant="body2" sx={{ fontSize: 11, color: b2bColors.textMuted }}>{l.sku}</Typography>}
                                    <Typography>{l.name || `Produit #${l.product_id}`}</Typography>
                                </TableCell>
                                <TableCell align="right" className="b2b-num">{l.quantity_ordered}</TableCell>
                                <TableCell align="right" className="b2b-num">{formatPrice(l.unit_price)}</TableCell>
                                <TableCell align="right" className="b2b-num">{formatPrice(l.tax_amount)}</TableCell>
                                <TableCell align="right" className="b2b-num" sx={{ fontWeight: 600 }}>{formatPrice(l.line_total)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Sous-total</Typography>
                        <Typography className="b2b-num">{formatPrice(order.subtotal)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Taxes</Typography>
                        <Typography className="b2b-num">{formatPrice(order.tax)}</Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ fontWeight: 600 }}>Total</Typography>
                        <Typography className="b2b-num" sx={{ fontWeight: 700, fontSize: 18 }}>{formatPrice(order.total)}</Typography>
                    </Stack>
                </Stack>
            </Paper>

            {order.shipping_address && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h5" sx={{ mb: 1 }}>Livraison</Typography>
                    <Typography variant="body2">
                        {typeof order.shipping_address === 'string' ? order.shipping_address :
                            [order.shipping_address.street, order.shipping_address.city, order.shipping_address.postalCode].filter(Boolean).join(', ')}
                    </Typography>
                </Paper>
            )}

            <Box>
                <Button component={RouterLink} to="/orders">← Retour à mes commandes</Button>
            </Box>
        </Stack>
    );
}
